<template>
    <component :is="tag">
        <AccordionItem v-if="!unidirectional" tag="div" :name="`route-${route.noParcours}`" class="route-accordion-item" :class="serviceTypeInfos.class">
            <template #title>
                <div class="route-button">
                    <div class="route-button-text">
                        <span class="no-route">
                            <span class="visuallyhidden">{{ t('route') }}&#32;</span>
                            {{ route.noParcours }}
                        </span>
                        <span v-if="route.accessible" class="accessible-icon">
                            <img src="@@/img/accessible.svg" width="20" height="20" />
                            <span class="visuallyhidden">{{ $t('a11y.accessible.accessible') }}</span>
                        </span>
                        <span v-else class="visuallyhidden">{{ $t('a11y.accessible.not_accessible') }}</span>
                        <span v-if="hasRouteNotices(route.noParcours)" class="notice-icon flex-container">
                            <img src="@@/img/alert.svg" width="20" height="20" />
                            <span class="visuallyhidden">{{ $t('a11y.route_with_notices') }}</span>
                        </span>
                    </div>
                    <IconDropdownArrow class="chevron-icon" width="30" height="30" />
                </div>
            </template>
            <template #content>
                <ul class="no-bullet directions-list" :class="serviceTypeInfos.class">
                    <li v-if="route.descriptionDirectionPrincipale">
                        <RouterLink
                            class="direction-item"
                            :to="{
                                name: routeName,
                                params: {
                                    route: route.noParcours,
                                    direction: directionSlug(route.codeDirectionPrincipale, route.descriptionDirectionPrincipale),
                                    date,
                                },
                            }"
                        >
                            <span>
                                <span class="visuallyhidden">Direction </span>
                                {{ t('to', { direction: route.descriptionDirectionPrincipale }) }}
                            </span>
                            <IconDropdownArrow class="chevron-right" width="10" height="16" />
                        </RouterLink>
                    </li>
                    <li v-if="route.descriptionDirectionRetour">
                        <RouterLink
                            class="direction-item"
                            :to="{
                                name: routeName,
                                params: {
                                    route: route.noParcours,
                                    direction: directionSlug(route.codeDirectionRetour, route.descriptionDirectionRetour),
                                    date,
                                },
                            }"
                        >
                            <span>
                                <span class="visuallyhidden">Direction </span>
                                {{ t('to', { direction: route.descriptionDirectionRetour }) }}
                            </span>
                            <IconDropdownArrow class="chevron-right" width="10" height="16" />
                        </RouterLink>
                    </li>
                </ul>
            </template>
        </AccordionItem>
        <div v-else :class="serviceTypeInfos.class">
            <RouterLink
                class="route-button unidirectional"
                :to="{
                    name: routeName,
                    params: {
                        route: route.noParcours,
                        direction: unidirectionnalDirectionParam,
                        date,
                    },
                }"
            >
                <div class="route-button-text">
                    <span class="no-route">
                        <span class="visuallyhidden">{{ t('route') }}&#32;</span>
                        {{ route.noParcours }}
                    </span>
                    <span v-if="route.accessible" class="accessible-icon">
                        <img src="@@/img/accessible.svg" width="20" height="20" />
                        <span class="visuallyhidden">{{ $t('a11y.accessible.accessible') }}</span>
                    </span>
                    <span v-else class="visuallyhidden">{{ $t('a11y.accessible.not_accessible') }}</span>
                    <span>{{ t('to', { direction: unidirectionnalDirection }) }}</span>
                </div>
                <IconDropdownArrow class="chevron-icon" width="30" height="30" />
            </RouterLink>
        </div>
    </component>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';

import directionSlug from '@@/js/utils/directionSlug';
import { useSchedulesStore } from '@@/js/stores/schedules';
import AccordionItem from '@/shared/AccordionItem';

import IconDropdownArrow from '@@/icons/dropdown-arrow.svg';

const props = defineProps({
    tag: {
        type: String,
        default: 'li',
    },
    route: {
        type: Object,
        default: () => ({}),
    },
    serviceType: {
        type: Number,
        default: 0,
    },
    serviceTypeInfos: {
        type: Object,
        default: () => ({}),
    },
    routeName: {
        type: String,
        default: 'route-single',
    },
    date: {
        type: String,
        default: undefined,
    },
});

const { t } = useI18n({
    messages: {
        fr: {
            to: 'Vers {direction}',
            route: 'Parcours',
        },
        en: {
            to: 'To {direction}',
            route: 'Route',
        },
    },
});

const { hasRouteNotices } = useSchedulesStore();

const unidirectional = computed(() => {
    return props.route.descriptionDirectionPrincipale === null || props.route.descriptionDirectionRetour === null;
});

const unidirectionnalDirection = computed(
    () => props.route.descriptionDirectionPrincipale || props.route.descriptionDirectionRetour,
);

const unidirectionnalDirectionParam = computed(() => (props.route.descriptionDirectionPrincipale
    ? directionSlug(props.route.codeDirectionPrincipale, props.route.descriptionDirectionPrincipale)
    : directionSlug(props.route.codeDirectionRetour, props.route.descriptionDirectionRetour)));
</script>

<style lang="scss" scoped>
.route-accordion-item {
    @include colorize {
        :deep(.accordion-button) {
            border-bottom: 1px solid get-color-variation('light');
            background-color: get-color-variation('base');
            &.is-open,
            &:hover {
                background-color: get-color-variation('hover');
            }
        }
    }
}
.route-button {
    @include accessible-focus;
    width: 100%;
    padding: 8px 16px;
    text-align: left;
    color: white;
    transition: all 300ms ease;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.route-button-text {
    display: flex;
    align-items: center;
}
.no-route {
    @include rem-typo(24px);
    font-weight: 700;
    margin-right: 12px;
}
.accessible-icon,
.notice-icon {
    display: flex;
    margin-right: 12px;
    flex-shrink: 0;
}
.chevron-icon {
    flex-shrink: 0;
    margin-left: 8px;
    padding: 8px 7px 5px;
    border-radius: 50%;
    transition: all 300ms ease;
}
@include colorize {
    .chevron-icon {
        background-color: get-color-variation('dark');
    }
    &.is-open .chevron-icon {
        fill: get-color-variation('base');
        background-color: $color-white;
        transform: rotate(180deg);
    }
}
.chevron-right {
    margin-right: 6px;
    margin-left: 6px;
    transform: rotate(-90deg);
    flex-shrink: 0;
}
.directions-list {
    margin: 0;
}
.direction-item {
    @include accessible-focus;
    padding: 8px 20px 8px 16px;
    text-decoration: none;
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
@include colorize {
    .direction-item,
    .unidirectional {
        border-bottom: 1px solid get-color-variation('base');
        background-color: get-color-variation('hover');
        &:hover {
            background-color: get-color-variation('dark');
        }
    }
}
.unidirectional {
    @include accessible-focus;
    @include rem-typo(13px);
    text-decoration: none;
    .chevron-icon {
        transform: rotate(-90deg);
    }
    @include breakpoint(medium) {
        @include rem-typo(16px);
    }
}
</style>
